import { Component, computed, effect, inject, input, model, OnInit, signal, untracked } from '@angular/core';
import { BlobFilesService } from '../../../services/blob-files.service';
import { SelectedFile } from '../../../models/client-models/selected-file';
import { SelectedFileType } from '../../../models/client-models/enums/selected-file-type';
import { UploadedFile } from '../../../models/server-models/entities/uploaded-file';
import { ErrorService } from '../../../../../shared/services/error.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '../../../services/toast.service';
import { PaperSideType } from '../../../models/server-models/enums/paper-side-type';
import { ExpandedPrintVariation } from '../../../models/client-models/entities/expanded-print-variation';
import { PrintVariationType } from '../../../models/server-models/enums/print-variation-type';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-files-selection',
  templateUrl: './files-selection.component.html',
  styleUrls: ['./files-selection.component.scss'],
  standalone: false,
})
export class FilesSelectionComponent {
  uploadFilesService = inject(BlobFilesService);
  errorService = inject(ErrorService);
  toastService = inject(ToastService);
  translate = inject(TranslateService);

  allowedMaxFilesQuantity = 50;
  allowedFilesSizeInMb = 20;
  allowedFilesSize = this.allowedFilesSizeInMb * 1024 * 1024;

  allowedFilesQuantity = computed(() => this.getAllowedFilesQuantity());

  allowedFileTypes = computed(() => this.getAllowedFileTypes());
  allowedFileTypesForInput = computed(() => this.allowedFileTypes().join(','));

  selectedFiles = signal<SelectedFile[]>([]);
  selectedFilesQuantity = computed(() => this.selectedFiles().length);

  progress = signal<number>(0);
  errors = signal<string[]>([]);
  isUploading = signal<boolean>(false);

  generalNumberOfCopies = signal<number>(1);

  selectedVariation = input.required<ExpandedPrintVariation | null>();
  selectedPaperSideType = input.required<PaperSideType | null>();

  constructor() {
    effect(() => {
      let errors = this.errors();

      if (errors.length > 0) {
        this.toastService.error(errors.join(', '));
      }
    });

    effect(() => {
      const copies = this.generalNumberOfCopies();
      untracked(() => this.updateSelectedFilesCopies(copies));
    });
  }

  onFileSelected(event: any): void {
    this.errors.set([]);
    const files: FileList = event.target.files;

    let totalSize = this.selectedFiles().reduce((sum, f) => sum + f.file.size, 0);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (this.selectedFiles().some((f) => f.name === file.name)) {
        continue;
      }

      if (this.selectedFiles().length >= this.allowedFilesQuantity()) {
        this.errors.set([
          `${this.translate.instant('UPLOAD.MAXIMUM')} ${this.allowedFilesQuantity()} ${this.translate.instant(
            'UPLOAD.ALLOWED'
          )}.`,
        ]);
        break;
      }

      // Validate file type
      if (!this.allowedFileTypes().includes(file.type)) {
        this.errors.set([...this.errors(), `${this.translate.instant('UPLOAD.FILE.INVALID')}: ${file.name}`]);
        continue;
      }

      // Check total size
      if (totalSize + file.size > this.allowedFilesSize) {
        this.errors.set([
          ...this.errors(),
          `${this.translate.instant('UPLOAD.TOTAL.FILE.SIZE.EXCEEDS')} ${
            this.allowedFilesSizeInMb
          }MB`,
        ]);
        break;
      }

      totalSize += file.size;

      this.selectedFiles.set([
        ...this.selectedFiles(),
        {
          file,
          name: file.name,
          size: Math.round((file.size / (1024 * 1024)) * 100) / 100,
          copies: 1,
          type: file.type.includes('pdf') ? SelectedFileType.Document : SelectedFileType.Image,
        },
      ]);
    }

    // Clear the file input so the same file can be reselected
    event.target.value = '';
  }

  onFileRemove(file: SelectedFile): void {
    this.selectedFiles.set(this.selectedFiles().filter((f) => f !== file));
  }

  shouldShowFileTypeHintMessage() {
    return this.isBookOrGeneralPrint() && this.isDoubleSided();
  }

  getSelectedFileIcon(selectedFile: SelectedFile) {
    return selectedFile.type == SelectedFileType.Document ? 'document-text-outline' : 'image-outline';
  }

  increaseCopies(file: SelectedFile) {
    this.selectedFiles.set(this.selectedFiles().map((f) => (f === file ? { ...f, copies: f.copies + 1 } : f)));
  }

  decreaseCopies(file: SelectedFile) {
    if (file.copies > 1) {
      this.selectedFiles.set(this.selectedFiles().map((f) => (f === file ? { ...f, copies: f.copies - 1 } : f)));
    }
  }

  decreaseGeneralCopies() {
    if (this.generalNumberOfCopies() > 1) {
      this.generalNumberOfCopies.set(this.generalNumberOfCopies() - 1);
    }
  }

  increaseGeneralCopies() {
    this.generalNumberOfCopies.set(this.generalNumberOfCopies() + 1);
  }

  async uploadFiles(): Promise<UploadedFile[]> {
    if (this.selectedFiles().length === 0) {
      return [];
    }

    this.isUploading.set(true);

    await new Promise((resolve) => setTimeout(resolve, 1000));

    this.progress.set(0);

    return new Promise<UploadedFile[]>((resolve, reject) => {
      this.uploadFilesService.uploadFiles(this.selectedFiles()).subscribe({
        next: (event) => {
          if (event.progress !== undefined) {
            this.progress.set(event.progress);
          } else if (event.uploadedFiles) {
            let uploadedFiles = event.uploadedFiles.map((uploadedFile) => {
              const selectedFile = this.selectedFiles().find((f) => f.name === uploadedFile.fileName);

              if (!selectedFile) {
                throw new Error(this.translate.instant('UPLOAD.FILE.MAP.ERROR'));
              }

              uploadedFile.copies = selectedFile.copies;

              return uploadedFile;
            });

            resolve(uploadedFiles);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.errorService.handleError(error);
          reject(null);
          this.uploadFinished();
        },
        complete: () => {
          this.uploadFinished();
        },
      });
    });
  }

  cleanUpFiles(): void {
    this.selectedFiles.set([]);
  }

  private uploadFinished() {
    this.progress.set(0);
    this.isUploading.set(false);
  }

  private getAllowedFileTypes(): string[] {
    if (this.isBookOrGeneralPrint()) {
      if (this.isSingleSided()) {
        return this.allFiles();
      } else {
        return this.onlyDocuments();
      }
    }

    return this.allFiles();
  }

  private getAllowedFilesQuantity(): number {
    if (this.isBusinessCard()) {
      return 2;
    }

    if (this.isCertificate()) {
      return 1;
    }

    return this.allowedMaxFilesQuantity;
  }

  private isBookOrGeneralPrint() {
    const variationType = this.selectedVariation()?.type;

    return variationType == PrintVariationType.Book || variationType == PrintVariationType.GeneralPrint;
  }

  isCertificateOrBusinessCard() {
    return this.isCertificate() || this.isBusinessCard();
  }

  isCertificate() {
    const variationType = this.selectedVariation()?.type;

    return variationType == PrintVariationType.Certificate;
  }

  isBusinessCard() {
    const variationType = this.selectedVariation()?.type;

    return variationType == PrintVariationType.BusinessCard;
  }

  private isSingleSided() {
    return this.selectedPaperSideType() == PaperSideType.SingleSided;
  }

  private isDoubleSided() {
    return this.selectedPaperSideType() == PaperSideType.DoubleSided;
  }

  private onlyDocuments() {
    return ['application/pdf'];
  }

  private onlyImages() {
    return ['image/jpeg', 'image/jpg', 'image/png'];
  }

  private allFiles() {
    return ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  }

  private updateSelectedFilesCopies(copies: number) {
    const updatedFiles = this.selectedFiles().map((selectedFile) => ({
      ...selectedFile,
      copies: copies,
    }));
    this.selectedFiles.set(updatedFiles);
  }
}
